import React, { useEffect, useState } from "react";
import {
  Alert,
  Modal,
  ModalBody,
  ModalHeader,
  Placeholder,
  PlaceholderButton,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import StudioMembershipCard from "./StudioMembershipCard";
import { setAuthorization } from "../../helpers/api_helper";
import {
  setStudioSwitchBanner,
  StudioMembership,
} from "../../helpers/identity_storage";
import { resolvePathAfterTenantSwitch } from "../../helpers/studioNavigation";
import {
  acceptMembership,
  declineMembership,
  getAuthSession,
  getMemberships,
  switchTenant,
} from "../../services/authService";
import { loginSuccess } from "../../slices/auth/login/reducer";

interface StudioSwitcherModalProps {
  isOpen: boolean;
  toggle: () => void;
  highlightMembershipId?: number | null;
}

const MembershipSkeleton = () => (
  <div className="col-md-6">
    <div className="border rounded p-3 h-100">
      <div className="d-flex gap-2 mb-3">
        <Placeholder animation="glow">
          <Placeholder
            className="rounded"
            style={{ width: 40, height: 40, display: "block" }}
          />
        </Placeholder>
        <div className="flex-grow-1">
          <Placeholder animation="glow">
            <Placeholder xs={8} className="mb-2" />
            <Placeholder xs={4} />
          </Placeholder>
        </div>
      </div>
      <Placeholder animation="glow">
        <Placeholder xs={10} className="mb-3" />
        <PlaceholderButton color="primary" className="w-100" disabled>
          {" "}
        </PlaceholderButton>
      </Placeholder>
    </div>
  </div>
);

const StudioSwitcherModal = ({
  isOpen,
  toggle,
  highlightMembershipId = null,
}: StudioSwitcherModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [memberships, setMemberships] = useState<StudioMembership[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyTenantId, setBusyTenantId] = useState<number | null>(null);
  const [busyMembershipId, setBusyMembershipId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const reloadMemberships = async () => {
    const response = await getMemberships();

    if (response.success && response.data) {
      setMemberships(response.data.memberships || []);
      setCurrentTenantId(response.data.current_tenant_id);
      return true;
    }

    setErrorMessage(
      response.message ||
        response.error?.message ||
        t("auth.switch_studio.loadError")
    );
    return false;
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErrorMessage("");

        try {
        const ok = await reloadMemberships();

        if (cancelled) {
          return;
        }

        if (!ok) {
          // errorMessage already set
        }
      } catch {
        if (!cancelled) {
          setErrorMessage(t("auth.switch_studio.loadError"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when modal opens
  }, [isOpen, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busyTenantId && !busyMembershipId) {
        toggle();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [busyMembershipId, busyTenantId, isOpen, toggle]);

  const handleSelect = async (membership: StudioMembership) => {
    if (membership.tenant_id === currentTenantId) {
      toggle();
      return;
    }

    if (membership.blocked || membership.status !== "active") {
      return;
    }

    setBusyTenantId(membership.tenant_id);
    setErrorMessage("");

    try {
      const response = await switchTenant(membership.tenant_id);
      const session = getAuthSession();

      if (response.success && session?.token) {
        const studioName =
          membership.tenant_name || t("auth.select_studio.unnamed");

        setStudioSwitchBanner(studioName);
        toast.success(
          response.message || t("auth.switch_studio.success", { name: studioName })
        );

        setAuthorization(session.token);
        dispatch(loginSuccess(session));
        toggle();
        navigate(resolvePathAfterTenantSwitch(location.pathname), { replace: true });
        return;
      }

      const message =
        response.message ||
        response.error?.message ||
        t("auth.switch_studio.genericError");
      setErrorMessage(message);
      toast.error(message);
    } catch {
      const message = t("auth.switch_studio.genericError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyTenantId(null);
    }
  };

  const handleAccept = async (membership: StudioMembership) => {
    if (!membership.id) {
      return;
    }

    setBusyMembershipId(membership.id);
    setErrorMessage("");

    try {
      const response = await acceptMembership(membership.id);

      if (!response.success || !response.data) {
        const message =
          response.message ||
          response.error?.message ||
          t("auth.select_studio.inviteError");
        setErrorMessage(message);
        toast.error(message);
        return;
      }

      toast.success(response.message || t("auth.select_studio.inviteAccepted"));

      if (response.data.access_token) {
        const session = getAuthSession();

        if (session?.token) {
          setAuthorization(session.token);
          dispatch(loginSuccess(session));
          toggle();
          navigate("/dashboard", { replace: true });
          return;
        }
      }

      await reloadMemberships();
      window.dispatchEvent(new CustomEvent("adatis:memberships:changed"));
      setBusyMembershipId(null);

      const studioName =
        membership.tenant_name || t("auth.select_studio.unnamed");
      const enterPrompt = await Swal.fire({
        title: t("auth.switch_studio.enter_after_accept_title"),
        text: t("auth.switch_studio.enter_after_accept_text", { name: studioName }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("auth.switch_studio.enter_after_accept_confirm"),
        cancelButtonText: t("auth.switch_studio.enter_after_accept_cancel"),
        confirmButtonColor: "#405189",
        cancelButtonColor: "#878a99",
        focusConfirm: true,
      });

      if (enterPrompt.isConfirmed) {
        await handleSelect({ ...membership, status: "active" });
      }
    } catch {
      const message = t("auth.select_studio.inviteError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyMembershipId(null);
    }
  };

  const handleDecline = async (membership: StudioMembership) => {
    if (!membership.id) {
      return;
    }

    const studioName = membership.tenant_name || t("auth.select_studio.unnamed");
    const confirmation = await Swal.fire({
      title: t("auth.select_studio.decline_confirm_title"),
      text: t("auth.select_studio.decline_confirm", { name: studioName }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("auth.select_studio.decline_invite"),
      cancelButtonText: t("auth.select_studio.decline_cancel"),
      confirmButtonColor: "#f06548",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setBusyMembershipId(membership.id);
    setErrorMessage("");

    try {
      const response = await declineMembership(membership.id);

      if (!response.success || !response.data) {
        const message =
          response.message ||
          response.error?.message ||
          t("auth.select_studio.inviteError");
        setErrorMessage(message);
        toast.error(message);
        return;
      }

      toast.success(response.message || t("auth.select_studio.inviteDeclined"));
      await reloadMemberships();
    } catch {
      const message = t("auth.select_studio.inviteError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyMembershipId(null);
    }
  };

  const visible = memberships
    .filter((item) => item.status === "active" || item.status === "pending")
    .slice()
    .sort((a, b) => {
      if (highlightMembershipId == null) {
        return 0;
      }

      const aMatch = a.id === highlightMembershipId ? 0 : 1;
      const bMatch = b.id === highlightMembershipId ? 0 : 1;

      return aMatch - bMatch;
    });
  const busy = Boolean(busyTenantId || busyMembershipId);

  return (
    <Modal
      isOpen={isOpen}
      toggle={busy ? undefined : toggle}
      centered
      size="lg"
      backdrop={busy ? "static" : true}
      keyboard={!busy}
      aria-labelledby="studio-switcher-title"
    >
      <ModalHeader toggle={busy ? undefined : toggle} id="studio-switcher-title">
        {t("auth.switch_studio.title")}
      </ModalHeader>
      <ModalBody>
        {errorMessage ? <Alert color="danger">{errorMessage}</Alert> : null}

        {loading ? (
          <div className="row g-3" aria-busy="true" aria-live="polite">
            <span className="visually-hidden">{t("auth.switch_studio.loading")}</span>
            <MembershipSkeleton />
            <MembershipSkeleton />
          </div>
        ) : visible.length === 0 ? (
          <Alert color="info" className="mb-0">
            {t("auth.switch_studio.singleStudio")}
          </Alert>
        ) : visible.filter((item) => item.status === "active").length <= 1 &&
          visible.every((item) => item.status !== "pending") ? (
          <Alert color="info" className="mb-0">
            {t("auth.switch_studio.singleStudio")}
          </Alert>
        ) : (
          <div className="row g-3">
            {visible.map((membership) => (
              <div className="col-md-6" key={`${membership.id ?? membership.tenant_id}-${membership.status}`}>
                <StudioMembershipCard
                  membership={membership}
                  highlighted={
                    membership.id === highlightMembershipId ||
                    (highlightMembershipId == null &&
                      membership.tenant_id === currentTenantId)
                  }
                  current={membership.tenant_id === currentTenantId}
                  busy={
                    busyTenantId === membership.tenant_id ||
                    busyMembershipId === membership.id
                  }
                  onSelect={handleSelect}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />
              </div>
            ))}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default StudioSwitcherModal;
